// getResourceUrl.js
'use strict';
import {
  API,
  API_V2,
  WATCH,
  PUBLIC,
  RSS_FEED,
} from './resourceUrlTypeConstants.js';
import {
  getIsInBrowserMainThread,
  getIsInNode,
  getIsInServiceWorker,
} from './getJsEnvironment.js';

const isServer = getIsInNode();
const isBrowserMainThread = getIsInBrowserMainThread();
const isServiceWorker = getIsInServiceWorker();
const FALLBACK_URL = 'https://api.swag.live';
const V2_FALLBACK_URL = 'https://api-v2.swag.live';

const PREFIX_MAP = {
  [API]:
    process.env.SSR_API_URL_PREFIX ||
    process.env.API_URL_PREFIX ||
    FALLBACK_URL,
  [API_V2]:
    process.env.SSR_API_V2_URL_PREFIX ||
    process.env.API_V2_URL_PREFIX ||
    V2_FALLBACK_URL,
  [WATCH]: process.env.WATCH_URL_PREFIX,
  [PUBLIC]: process.env.PUBLIC_URL_PREFIX,
  [RSS_FEED]: process.env.RSS_FEED_URL_PREFIX,
};

const getHostname = () => 'swag.live';

/**
 * Get api endpoint core
 * @param {String} {[path = '/']} API Suffix Path
 * @param {String} {[endpoint = '/']} API Endpoint
 * @param {String} {resourceType} API Resource Type
 * @return {URL instance} will return URL instance.
 */
const getResourceUrlCore = ({ endpoint = '/', path, resourceType = API }) => {
  const fallbackResourceType = API_V2 === resourceType ? API_V2 : API;

  let subDomain = PREFIX_MAP[resourceType] || PREFIX_MAP[fallbackResourceType];

  if ((isServer || isServiceWorker) && globalThis?.__RESOURCE_URL__) {
    subDomain =
      globalThis.__RESOURCE_URL__[resourceType] ||
      globalThis.__RESOURCE_URL__[fallbackResourceType] ||
      subDomain;
  }
  if (isBrowserMainThread) {
    subDomain =
      window.__RESOURCE_URL__?.[resourceType] ||
      window.__RESOURCE_URL__?.[fallbackResourceType] ||
      subDomain;
  }

  const formattedPath = path
    ? path.replace(/^\//, '').replace(/[/]*$/, '/')
    : '';
  const formattedEndpoint = endpoint.replace(/^\//, '');

  let isPrefixHasProtocol = false;
  try {
    isPrefixHasProtocol = !!new URL(subDomain).protocol;
  } catch (error) {
    //
  }

  if (isPrefixHasProtocol) {
    // ex: http://proxy.web
    return new URL(formattedEndpoint, new URL(formattedPath, subDomain));
  } else {
    const domain = getHostname();
    return new URL(
      formattedEndpoint,
      new URL(formattedPath, `https://${subDomain}.${domain}`)
    );
  }
};

/**
 * Get api endpoint
 * @param {String} {[path = '/']} API Suffix Path
 * @param {String} {[endpoint = '/']} API Endpoint
 * @param {String} {[resourceType = API]} API Resource Type
 * @param {String} {origin} customize origin string
 * @return {URL instance} will return URL instance.
 */
const getResourceUrl = ({
  path = '',
  endpoint,
  resourceType = API,
  origin,
}) => {
  const isApi = [API, API_V2].includes(resourceType);
  const url = getResourceUrlCore({ endpoint, path, resourceType });
  if (origin) {
    url.searchParams.set('_o', origin);
  } else if (
    isBrowserMainThread &&
    isApi &&
    // localhost is for skip unit test env
    !['swag.live', 'localhost'].includes(window.location.hostname)
  ) {
    // try to imitate `Vary` header to escape CORS.
    url.searchParams.set('_o', window.location.origin);
  }
  return url;
};

export default getResourceUrl;
